import { useState } from 'react';

import type { OrderStatusFilter } from '@solar/api-client';
import {
  APT_TYPE_LABEL,
  ErrorBox,
  ORDER_STATE_LABEL,
  Spinner,
  formatDate,
  displayAddress,
  formatPhone,
  useAsync,
  won,
} from '@solar/ui';

import { api } from '../api';
import { ExcelButton } from '../ExcelButton';
import { BatchBar, hasBatchActions } from './BatchActions';

const FILTERS: Array<{ value: OrderStatusFilter; label: string }> = [
  { value: 'all', label: '전체' },
  { value: 'incomplete', label: '미완료' },
  { value: 'completed', label: '완료' },
  { value: 'unpaid', label: '미수납' },
  { value: 'canceled', label: '취소' },
];

const SIZE = 30;

/** 주문 등록 화면이 열리는 역할 — 서버 ORDER_CREATE_ROLES(1,2,3,5)와 동일. */
const CREATE_ROLES = [1, 2, 3, 5];

/** 주문 엑셀 버튼 노출 역할 — 레거시 saveExcel 진입 화면(통계 허용 역할과 동일). */
const EXCEL_ROLES = [1, 2, 3, 5];

/** 지급내역서(정산) 엑셀 — 서버 SETTLEMENT_EXCEL_ROLES(1,2,6)와 동일. */
const SETTLEMENT_EXCEL_ROLES = [1, 2, 6];

export function OrderTable({
  role,
  selected,
  onSelect,
  onCreate,
  initialStatus = 'all',
}: {
  role: number;
  selected: number | null;
  onSelect: (id: number) => void;
  onCreate: () => void;
  /** 대시보드 카드에서 넘어올 때의 초기 필터. */
  initialStatus?: OrderStatusFilter;
}) {
  const [status, setStatus] = useState<OrderStatusFilter>(initialStatus);
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);

  // 일괄 처리 대상 선택. 페이지·필터가 바뀌어도 유지된다 —
  // 레거시도 체크박스 상태와 무관하게 ids 만 모아 보냈다.
  const [checked, setChecked] = useState<ReadonlySet<number>>(new Set());
  const [okMessage, setOkMessage] = useState<string | null>(null);
  const batchable = hasBatchActions(role);

  // 목록과 엑셀이 같은 조건을 보도록 필터를 한 곳에서 만든다.
  const listQuery = {
    status,
    keyword: applied,
    assigned_to_me: false,
    include_canceled: status === 'canceled' || status === 'all',
  };

  // 관리자는 회사 전체를 본다 — 어느 데이터까지 보이는지는 서버의
  // apply_company_scope() 가 결정한다. 레거시 CompanyScope 는 Laravel 밖에서
  // 동작하지 않아 JSP·배치 쿼리에서는 격리가 아예 없었다.
  const orders = useAsync(
    () => api.listOrders({ ...listQuery, page, size: SIZE }),
    [status, applied, page],
  );

  function changeFilter(value: OrderStatusFilter) {
    setStatus(value);
    setPage(1);
  }

  function toggle(id: number) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function togglePage(select: boolean) {
    const pageIds = orders.data?.items.map((o) => o.id) ?? [];
    setChecked((prev) => {
      const next = new Set(prev);
      for (const id of pageIds) {
        if (select) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  const total = orders.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));
  const pageIds = orders.data?.items.map((o) => o.id) ?? [];
  const allChecked = pageIds.length > 0 && pageIds.every((id) => checked.has(id));

  return (
    <>
      <div className="toolbar">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={status === f.value ? 'chip active' : 'chip'}
            onClick={() => changeFilter(f.value)}
          >
            {f.label}
          </button>
        ))}

        <form
          className="search"
          onSubmit={(e) => {
            e.preventDefault();
            setApplied(keyword.trim());
            setPage(1);
          }}
        >
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="이름 · 연락처 · 주소"
          />
          <button type="submit">검색</button>
        </form>

        {CREATE_ROLES.includes(role) && (
          <button type="button" className="primary create" onClick={onCreate}>
            주문 등록
          </button>
        )}

        {/* 레거시 btnType=download — 현재 목록 필터 그대로 내려받는다. */}
        {EXCEL_ROLES.includes(role) && (
          <ExcelButton
            label="엑셀 다운로드"
            fallback="orders.xlsx"
            fetcher={() => api.ordersExcel(listQuery)}
          />
        )}
        {/* 레거시 btnType=payment — order/list.blade.php:251-255 의 1,2,6 노출 조건. */}
        {SETTLEMENT_EXCEL_ROLES.includes(role) && (
          <ExcelButton
            label="정산 엑셀"
            fallback="settlement.xlsx"
            fetcher={() => api.settlementExcel(listQuery)}
          />
        )}
      </div>

      {batchable && (
        <BatchBar
          role={role}
          ids={[...checked]}
          onDone={(msg) => {
            setOkMessage(msg);
            setChecked(new Set());
            orders.reload();
          }}
        />
      )}
      {okMessage !== null && <p className="ok">{okMessage}</p>}

      {orders.loading && <Spinner />}
      {orders.error && <ErrorBox message={orders.error} onRetry={orders.reload} />}

      {orders.data && (
        <table className="grid">
          <thead>
            <tr>
              {batchable && (
                <th className="check-col">
                  <input
                    type="checkbox"
                    aria-label="현재 페이지 전체 선택"
                    checked={allChecked}
                    onChange={(e) => togglePage(e.target.checked)}
                  />
                </th>
              )}
              <th>신청인</th>
              <th>연락처</th>
              <th>주소</th>
              <th>유형</th>
              <th>설치일</th>
              <th>상태</th>
              <th>기사</th>
              <th className="num">자부담</th>
              <th>수납</th>
            </tr>
          </thead>
          <tbody>
            {orders.data.items.length === 0 && (
              <tr>
                <td colSpan={batchable ? 10 : 9} className="muted">
                  조건에 맞는 주문이 없습니다.
                </td>
              </tr>
            )}
            {orders.data.items.map((order) => (
              <tr
                key={order.id}
                className={order.id === selected ? 'selected' : ''}
                onClick={() => onSelect(order.id)}
              >
                {batchable && (
                  <td className="check-col" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`주문 ${order.id} 선택`}
                      checked={checked.has(order.id)}
                      onChange={() => toggle(order.id)}
                    />
                  </td>
                )}
                <td>{order.user_name ?? '-'}</td>
                <td>{formatPhone(order.user_phone)}</td>
                <td className="addr">
                  {displayAddress(order.addr_full, order.building_name, order.apart_number)}
                </td>
                <td>{APT_TYPE_LABEL[order.apt_type ?? 0] ?? '-'}</td>
                <td>{formatDate(order.install_date)}</td>
                <td>
                  <span className={`badge s${order.state_type}`}>
                    {ORDER_STATE_LABEL[order.state_type] ?? order.state_type}
                  </span>
                </td>
                <td>{order.engineer_name ?? '-'}</td>
                <td className="num">{won(order.payment)}</td>
                <td>{order.payment_check === 'Y' ? '완료' : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="pager">
        <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
          이전
        </button>
        <span>
          {page} / {lastPage} (총 {total}건)
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
          다음
        </button>
      </div>
    </>
  );
}
